import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ACTIVITY_TYPES } from "./config";

const TYPE_BADGE = { note: "bg-badge text-sky-700", appel: "bg-emerald-50 text-emerald-700", courriel: "bg-amber-50 text-amber-700", rencontre: "bg-badge text-sky-700", autre: "bg-slate-100 text-slate-600", consentement: "bg-badge text-sky-700" };

export default function CrmActivities() {
  const [rows, setRows] = useState(null);
  const [type, setType] = useState("");
  const [entityId, setEntityId] = useState("");
  const [error, setError] = useState("");

  const load = useCallback(() => {
    if (!entityId && !type) { setRows(null); return; }
    const params = {};
    if (type) params.type = type;
    setRows(null);
    api.get("/crm/activities", { params: type ? params : { ...params, company_id: entityId } })
      .then(({ data }) => { setRows(data); setError(""); })
      .catch((e) => { setError(formatApiErrorDetail(e.response?.data?.detail)); setRows([]); });
  }, [type, entityId]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div className="space-y-6" data-testid="crm-activities-page">
      <div>
        <h1 className="font-display text-2xl font-bold text-navy-950">Activités</h1>
        <p className="mt-1 text-sm text-steel">Historique chronologique de toutes les interactions enregistrées dans le CRM.</p>
      </div>

      <div className="flex flex-wrap items-end gap-4 rounded-2xl border border-line bg-white p-5 shadow-card">
        <div>
          <label htmlFor="act-type" className="mb-1.5 block text-[13px] font-bold text-navy-900">Type</label>
          <select id="act-type" data-testid="crm-activities-type-filter" value={type} onChange={(e) => setType(e.target.value)}
            className="rounded-xl border border-line bg-white px-4 py-2.5 text-sm">
            <option value="">Tous</option>
            {ACTIVITY_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        {!type && (
          <div>
            <label htmlFor="act-entity" className="mb-1.5 block text-[13px] font-bold text-navy-900">Identifiant d'entité (entreprise…)</label>
            <input id="act-entity" data-testid="crm-activities-entity-input" value={entityId} onChange={(e) => setEntityId(e.target.value)}
              placeholder="ex. identifiant d'entreprise" className="w-72 rounded-xl border border-line bg-white px-4 py-2.5 text-sm outline-none focus:border-electric" />
          </div>
        )}
      </div>

      {error && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="crm-activities-error">{error}</p>}

      {rows === null ? (
        <p className="text-sm text-steel">Filtrez par type ou par entité pour afficher les activités.</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-steel" data-testid="crm-activities-empty">Aucune activité pour ce filtre.</p>
      ) : (
        <ol className="relative space-y-4 border-l border-line pl-6" data-testid="crm-activities-timeline">
          {rows.map((a) => (
            <li key={a.id} data-testid={`crm-activity-item-${a.id}`} className="relative rounded-2xl border border-line bg-white p-5 shadow-card">
              <span className={`absolute -left-[31px] top-5 h-2.5 w-2.5 rounded-full ring-4 ring-paper ${a.type === "appel" ? "bg-emerald-500" : a.type === "courriel" ? "bg-amber-500" : "bg-electric"}`} />
              <div className="flex flex-wrap items-center gap-2">
                <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase ${TYPE_BADGE[a.type] || TYPE_BADGE.autre}`}>{a.type}</span>
                <time className="text-xs text-steel">{new Date(a.created_at).toLocaleString("fr-CA")}</time>
              </div>
              <p className="mt-2 text-sm text-graphite">{a.content}</p>
              <p className="mt-1 text-xs text-steel">Par {a.created_by || "—"}{a.company_id ? ` · entreprise ${a.company_id.slice(0, 8)}…` : ""}</p>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
